import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ServiceSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [saving, setSaving] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(1);
  const [tab, setTab] = useState('settings');

  const fetchSettings = () => {
    if (!companyId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/services/settings`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSettings(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetchAudit = () => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/services/audit?page=${auditPage}&per_page=50`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => {
        setAuditLogs(Array.isArray(d.data) ? d.data : []);
        setAuditTotal(d.last_page || 1);
      })
      .catch(() => setAuditLogs([]));
  };

  useEffect(() => { fetchSettings(); }, [companyId]);
  useEffect(() => { if (tab === 'audit') fetchAudit(); }, [tab, auditPage, companyId]);

  const saveSettings = () => {
    if (!settings) return;
    setSaving(true);
    fetch(`${API}/companies/${companyId}/services/settings`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSettings(d); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const updateField = (field, value) => setSettings({ ...settings, [field]: value });

  const cardStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
  const btnStyle = (bg) => ({ padding: '4px 12px', background: bg, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', marginRight: 4 });
  const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
  const tabStyle = (active) => ({ padding: '10px 24px', background: active ? '#3b82f6' : '#f1f5f9', color: active ? '#fff' : '#475569', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' });
  const toggleStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' };

  if (initialLoading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 24px', fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>⚙️ Service Settings & History</h2>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 0 }}>
        <button onClick={() => setTab('settings')} style={tabStyle(tab === 'settings')}>Settings</button>
        <button onClick={() => setTab('audit')} style={tabStyle(tab === 'audit')}>Audit Log</button>
      </div>

      {tab === 'settings' && settings && (
        <>
          {/* Service Requirements */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📋 Service Requirements</h3>
            <div style={toggleStyle}>
              <div><span style={{ fontWeight: 600 }}>Require Service Owner</span><p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Services must have an owner assigned</p></div>
              <input type="checkbox" checked={!!settings.require_service_owner} onChange={e => updateField('require_service_owner', e.target.checked)} style={{ width: 20, height: 20 }} />
            </div>
            <div style={toggleStyle}>
              <div><span style={{ fontWeight: 600 }}>Require Category</span><p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Services must be assigned to a category</p></div>
              <input type="checkbox" checked={!!settings.require_category} onChange={e => updateField('require_category', e.target.checked)} style={{ width: 20, height: 20 }} />
            </div>
            <div style={toggleStyle}>
              <div><span style={{ fontWeight: 600 }}>Category Hierarchy</span><p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Enable sub-categories (unlimited levels)</p></div>
              <input type="checkbox" checked={!!settings.category_hierarchy_enabled} onChange={e => updateField('category_hierarchy_enabled', e.target.checked)} style={{ width: 20, height: 20 }} />
            </div>
            <div style={toggleStyle}>
              <div><span style={{ fontWeight: 600 }}>Allow Shared Services</span><p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Services can be shared across companies</p></div>
              <input type="checkbox" checked={!!settings.allow_shared_services} onChange={e => updateField('allow_shared_services', e.target.checked)} style={{ width: 20, height: 20 }} />
            </div>
          </div>

          {/* Engagement Rules */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>🔗 Engagement Rules</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Financial Link Requirement</label>
                <select value={settings.financial_link_requirement || 'optional'} onChange={e => updateField('financial_link_requirement', e.target.value)} style={inputStyle}>
                  <option value="optional">Optional</option>
                  <option value="required">Required</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Operational Link Requirement</label>
                <select value={settings.operational_link_requirement || 'optional'} onChange={e => updateField('operational_link_requirement', e.target.value)} style={inputStyle}>
                  <option value="optional">Optional</option>
                  <option value="required">Required</option>
                  <option value="none">None</option>
                </select>
              </div>
            </div>
            <div style={{ ...toggleStyle, marginTop: 12 }}>
              <div><span style={{ fontWeight: 600 }}>Allow Direct Engagement Creation</span><p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Users can create engagements without going through opportunities</p></div>
              <input type="checkbox" checked={!!settings.allow_direct_engagement} onChange={e => updateField('allow_direct_engagement', e.target.checked)} style={{ width: 20, height: 20 }} />
            </div>
          </div>

          {/* Lifecycle Behavior */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>🔄 Lifecycle Behavior</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Activation Behavior</label>
                <select value={settings.activation_behavior || 'warn'} onChange={e => updateField('activation_behavior', e.target.value)} style={inputStyle}>
                  <option value="warn">Warn on missing fields</option>
                  <option value="block">Block until complete</option>
                  <option value="allow">Allow without validation</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Completion Behavior</label>
                <select value={settings.completion_behavior || 'warn'} onChange={e => updateField('completion_behavior', e.target.value)} style={inputStyle}>
                  <option value="warn">Warn on outstanding items</option>
                  <option value="block">Block until resolved</option>
                  <option value="allow">Allow without validation</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>On Hold Max Days</label>
                <input type="number" value={settings.on_hold_max_days || ''} onChange={e => updateField('on_hold_max_days', e.target.value ? parseInt(e.target.value) : null)} style={inputStyle} placeholder="No limit" />
              </div>
            </div>
          </div>

          {/* Renewal Configuration */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>🔄 Renewal Configuration</h3>
            <div style={toggleStyle}>
              <div><span style={{ fontWeight: 600 }}>Enable Renewals</span><p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Track and manage engagement renewals</p></div>
              <input type="checkbox" checked={!!settings.renewal_enabled} onChange={e => updateField('renewal_enabled', e.target.checked)} style={{ width: 20, height: 20 }} />
            </div>
            {settings.renewal_enabled && (
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Reminder Days (comma-separated)</label>
                <input
                  value={Array.isArray(settings.renewal_reminder_days) ? settings.renewal_reminder_days.join(', ') : (settings.renewal_reminder_days || '')}
                  onChange={e => updateField('renewal_reminder_days', e.target.value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v)))}
                  style={inputStyle}
                  placeholder="30, 14, 7"
                />
              </div>
            )}
          </div>

          {/* Analytics Thresholds */}
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📊 Analytics Thresholds</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Margin Threshold (%)</label>
                <input type="number" step="0.1" value={settings.margin_threshold || ''} onChange={e => updateField('margin_threshold', parseFloat(e.target.value))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Win Rate Threshold (%)</label>
                <input type="number" step="0.1" value={settings.win_rate_threshold || ''} onChange={e => updateField('win_rate_threshold', parseFloat(e.target.value))} style={inputStyle} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={saveSettings} disabled={saving} style={{ padding: '10px 32px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </>
      )}

      {tab === 'audit' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📜 Audit Log</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Field</th>
                <th style={thStyle}>Old Value</th>
                <th style={thStyle}>New Value</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map(log => (
                <tr key={log.id}>
                  <td style={tdStyle}>{new Date(log.created_at).toLocaleDateString()}</td>
                  <td style={tdStyle}>{log.user?.name || '—'}</td>
                  <td style={tdStyle}>{log.action}</td>
                  <td style={{ ...tdStyle, fontSize: '0.75rem' }}>{(log.auditable_type || '').split('\\').pop()}</td>
                  <td style={tdStyle}>{log.field_name || '—'}</td>
                  <td style={{ ...tdStyle, color: '#94a3b8', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.old_value || '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.new_value || '—'}</td>
                </tr>
              ))}
              {auditLogs.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No audit records</td></tr>}
            </tbody>
          </table>
          {auditTotal > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button disabled={auditPage <= 1} onClick={() => setAuditPage(auditPage - 1)} style={{ ...btnStyle('#6b7280'), opacity: auditPage <= 1 ? 0.5 : 1 }}>Prev</button>
              <span style={{ padding: '4px 12px', fontSize: '0.85rem' }}>Page {auditPage} of {auditTotal}</span>
              <button disabled={auditPage >= auditTotal} onClick={() => setAuditPage(auditPage + 1)} style={{ ...btnStyle('#6b7280'), opacity: auditPage >= auditTotal ? 0.5 : 1 }}>Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
